/**
 * Form fields that affect slot availability.
 * When any of these fields changes, slot availability should be rechecked.
 *
 * This list is used for:
 * - Watching form changes with react-hook-form
 * - Determining when to refetch slot availability
 * - Dependency tracking in Order Management
 */
export const SLOT_MANAGEMENT_DEPENDENT_FIELDS = [
  'dateTime',
  'quantity',
  'plusLoadExpected',
  'material',
  'contract',
  'spacingOrFlowRate',
  'unloadingMethod',
  'truckType',
  'timeOnJobSite',
  'materialVariations'
] as const

/**
 * Type for slot management dependent field names
 */
export type SlotManagementDependentField = (typeof SLOT_MANAGEMENT_DEPENDENT_FIELDS)[number]

/**
 * Default time slot interval in minutes
 * Used when generating possible time slots
 */
export const DEFAULT_SLOT_INTERVAL_MINUTES = 15

/**
 * Slot management error codes mapped from backend responses
 */
export const SLOT_MANAGEMENT_ERROR_CODES = {
  /** No error - operation successful */
  NONE: 'none',
  /** OTV external service error (includes all OTV-related errors with dynamic messages) */
  OTV_SERVICE_ERROR: 'otvServiceError',
  /** Slot must be at least 90 minutes in the future */
  INSUFFICIENT_DELIVERY_WINDOW_GAP: 'insufficientDeliveryWindowGap',
  /** Slot is outside configured working hours */
  OUTSIDE_WORKING_HOURS: 'outsideWorkingHours',
  /** Insufficient lead time or non-working day */
  INSUFFICIENT_LEAD_TIME: 'insufficientLeadTime',
  /** Quantity exceeds maximum auto-confirmable amount */
  QUANTITY_EXCEEDS_MAXIMUM: 'quantityExceedsMaximum',
  /** Last truck time exceeds latest auto-confirm time */
  LATEST_TRUCK_TIME_EXCEEDED: 'latestTruckTimeExceeded',
  /** Batch processing error */
  BATCH_PROCESSING_ERROR: 'batchProcessingError',
  /** Unknown or unclassified error */
  UNKNOWN: 'unknown'
} as const
